import { Link, useNavigate } from "react-router-dom";
import {
  ReactElement,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import AnimatedBackButton from "../components/AnimatedBackButton";
import { usePlatform } from "components/utils/PlatformContext";
import { NeedHelpButton } from "../components/NeedHelpButton";

interface MainHeaderProps {
  ref: any;
  backButtonFunction?: () => void;
  headerOptions?: (ref: React.RefObject<any>) => JSX.Element;
}

const MainHeader: React.FC<MainHeaderProps> = forwardRef(
  ({ backButtonFunction, headerOptions }, ref) => {
    // States definition
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState<ReactElement | string>("");
    const [showBackButton, setShowBackButton] = useState(false);

    // Get the platform context
    const { logo } = usePlatform();

    // References definition
    const optionsRef: React.Ref<any> = useRef();

    // Define a navigation hook
    const navigate = useNavigate();

    /**
     * Define the list of functions that can be used from external components
     * using a reference
     */
    useImperativeHandle(ref, () => ({
      // Outer function to change the tab
      setHeaderTitle(title: string) {
        setTitle(title);
      },
      setHeaderDescription(description: string) {
        setDescription(description);
      },
      setHeaderBackButton(bool: boolean) {
        setShowBackButton(bool);
      },
      setShowBackButton(bool: boolean) {
        setShowBackButton(bool);
      },
      options() {
        return optionsRef.current;
      },
    }));

    /**
     * Function to execute on back button click
     */
    const onBackButtonClick = () => {
      // Execute the insterted backbuttonFunction
      if (backButtonFunction) backButtonFunction();
    };

    /**
     * Function executed when clicking on the top left logo
     */
    const onClickLogo = () => {
      // Ensure that the state is reset
      navigate("/home", { state: null });

      // And reloading the page
      window.location.reload();
    };

    return (
      <div className="flex flex-col gap-0">
        <div className="w-full h-24 justify-between items-center inline-flex">
          <div
            className={`grow shrink basis-0 justify-start items-center gap-3 flex`}>
            <Link
              reloadDocument
              to="/home">
              <div className="w-[124px] h-14 flex items-center justify-start">
                <img
                  className="w-auto h-auto max-w-full max-h-full object-contain"
                  alt="Platform logo"
                  src={logo}
                  onClick={onClickLogo}
                />
              </div>
            </Link>
            <div className="w-px self-stretch justify-start items-start gap-2 flex">
              <div className="w-px self-stretch bg-neutral-200" />
            </div>
            <div className="grow shrink basis-0 h-12 justify-start items-center gap-3 flex">
              <AnimatedBackButton
                show={showBackButton}
                onClick={onBackButtonClick}
              />
              <div
                role="main"
                className="grow flex-col justify-center items-start gap-0.5 inline-flex">
                <h1 className="text-neutral-600 text-[23px] font-bold font-sans leading-7 select-none">
                  {title}
                </h1>
                <h2 className="text-neutral-500 text-sm font-normal font-poppins leading-[17px] whitespace-nowrap select-none">
                  {description}
                </h2>
              </div>
              <NeedHelpButton />
            </div>
          </div>
          {/* Options button to be displayed */}
          {headerOptions && headerOptions(optionsRef)}
        </div>
      </div>
    );
  }
);

export default MainHeader;
